import {
  Autocomplete,
  Box,
  Stack,
  TextField,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Button,
  MenuItem,
  Select,
  InputLabel,
  Checkbox,
  FormGroup,
} from "@mui/material";
import { ColorsConstants, formConstants } from "../../../cssUtils/constants";
import typography from "../../../cssUtils/typography";
import { Info } from "@mui/icons-material";
import { useState, useEffect } from "react";
import { formatTitle } from "../../utils";
import React from "react";
import BackButton from "../../../common/back-button";

// A Switch whose cases hold buttons (the builder's "Conditional Next Screen"),
// as opposed to one that holds questions.
const isNavSwitch = (el) =>
  el?.type === "Switch" &&
  Object.values(el.cases || {}).some((c) => c.some((x) => x.type === "Footer"));

// Every element, including those inside Switch cases / If branches (fields
// shown only for certain answers).
const flattenElements = (elements) =>
  elements.flatMap((el) =>
    el.type === "Switch"
      ? flattenElements(Object.values(el.cases || {}).flat())
      : el.type === "If"
        ? flattenElements([...(el.then || []), ...(el.else || [])])
        : [el],
  );

// `compact` drops the heading and screen picker (the parent provides its own) and
// fits the phone to the width of its container.
const FormPreview = ({
  formJson,
  selectedScreenId,
  presetId,
  category,
  compact = false,
  onScreenChange,
}) => {
  const screens = formJson?.screens;

  const [formData, setFormData] = useState({});
  const [screenStack, setScreenStack] = useState([]);

  const currentScreenId = screenStack[screenStack.length - 1];

  const currentScreen = screens?.find(
    (screen) => screen.id === currentScreenId
  );
  
  // New form JSON: start over on the selected (or first) screen.
  useEffect(() => {
    if (screens?.length > 0) {
      setScreenStack([selectedScreenId || screens[0].id]);
      setFormData({});
    } else {
      setScreenStack([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formJson]);

  // A screen picked outside the preview: jump to it unless it is already showing.
  useEffect(() => {
    if (!selectedScreenId || !screens?.length) return;
    setScreenStack((prev) =>
      prev[prev.length - 1] === selectedScreenId ? prev : [selectedScreenId],
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedScreenId]);

  // Tell the parent which screen is showing, so its picker can follow.
  useEffect(() => {
    if (currentScreenId && onScreenChange) onScreenChange(currentScreenId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentScreenId]);

  const handleInputChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  
  const navigateToScreen = (id) => {
    setScreenStack(prev => [...prev, id]);
  };

  const goBack = () => {
    setScreenStack(prev => prev.slice(0, -1));
  };

  // "Show only when" rules, checked against answers typed so far.
  const isVisible = (element) =>
    (element["visible-when"] || []).every((rule) => {
      const answer = formData[rule.field];
      if ([">", "<", ">=", "<="].includes(rule.op)) {
        const a = parseFloat(answer);
        const b = parseFloat(rule.value);
        if (isNaN(a) || isNaN(b)) return false;
        return { ">": a > b, "<": a < b, ">=": a >= b, "<=": a <= b }[rule.op];
      }
      const same = String(answer ?? "") === String(rule.value ?? "");
      return rule.op === "!=" ? !same : same;
    });

  const renderFormElement = (element, index) => {
    if (!isVisible(element)) return null;
    switch (element.type) {
      case "TextHeading":
        return (
          <Typography
            key={index}
            sx={{ ...typography.h1, mb: 2 }}
          >
            {element.text}
          </Typography>
        );

      case "TextSubheading":
        return (
          <Typography
            key={index}
            sx={{ ...typography.h2, fontWeight: 600, mb: 1.5, mt: 2 }}
          >
            {element.text}
          </Typography>
        );

      case "TextBody":
        return (
          <Typography key={index} sx={{ ...typography.bodyLarge, mb: 2 }}>
            {element.text}
          </Typography>
        );

      case "TextCaption":
        return (
          <Typography
            key={index}
            sx={{ ...typography.bodySmall, mb: 1, color: ColorsConstants.primaryGray60 }}
          >
            {element.text}
          </Typography>
        );

      case "TextInput":
        return (
          <TextField
            key={index}
            label={element.label}
            name={element.name}
            required={element.required}
            fullWidth
            variant="outlined"
            size="small"
            type={
              { number: "number", email: "email", phone: "tel", password: "password", passcode: "password" }[
                element["input-type"]
              ] || "text"
            }
            placeholder={element.placeholder || ""}
            value={formData[element.name] || ""}
            onChange={(e) => handleInputChange(element.name, e.target.value)}
            inputProps={{
              minLength: element["min-chars"],
              maxLength: element["max-chars"],
              style: typography.bodyLarge,
            }}
            helperText={element["helper-text"]}
            FormHelperTextProps={{ sx: typography.bodySmall }}
            InputLabelProps={{ sx: typography.bodyLarge }}
            sx={{ 
              mb: 2,
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": {
                  borderColor: formConstants.greenColor,
                },
                "&.Mui-focused fieldset": {
                  borderColor: formConstants.greenColor,
                },
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: formConstants.greenColor,
              },
            }}
          />
        );

      case "TextArea":
        return (
          <TextField
            key={index}
            label={element.label}
            name={element.name}
            required={element.required}
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            size="small"
            placeholder={element.placeholder || ""}
            value={formData[element.name] || ""}
            onChange={(e) => handleInputChange(element.name, e.target.value)}
            inputProps={{
              minLength: element["min-chars"],
              maxLength: element["max-chars"],
              style: typography.bodyLarge,
            }}
            helperText={element["helper-text"]}
            FormHelperTextProps={{ sx: typography.bodySmall }}
            InputLabelProps={{ sx: typography.bodyLarge }}
            sx={{ 
              mb: 2, 
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": {
                  borderColor: formConstants.greenColor,
                },
                "&.Mui-focused fieldset": {
                  borderColor: formConstants.greenColor,
                },
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: formConstants.greenColor,
              },
            }}
          />
        );

      case "RadioButtonsGroup":
        return (
          <FormControl
            key={index}
            component="fieldset"
            sx={{
              mb: 2,
              width: "100%",
              "& .MuiFormLabel-root.Mui-focused": {
                color: "#000000",
              },
            }}
          >
            <FormLabel component="legend" sx={{ ...typography.bodyLarge, mb: 1 }}>
              {element.label}
              {element.required && (
                <Typography component="span" color="error" sx={typography.bodyLarge}>
                  {" "}
                  *
                </Typography>
              )}
            </FormLabel>

            <RadioGroup
              name={element.name}
              value={formData[element.name] || ""}
              onChange={(e) => handleInputChange(element.name, e.target.value)}
            >
              {element["data-source"]?.map((option) => (
                <FormControlLabel
                  key={option.id}
                  value={option.id}
                  labelPlacement="start"
                  control={
                    <Radio
                      sx={{
                        color: ColorsConstants.primaryGray20, 
                        "&.Mui-checked": {
                          color: formConstants.greenButton, 
                        },
                        "&.Mui-focusVisible": {
                          outline: `2px solid ${formConstants.greenButton}`,
                          outlineOffset: 2,
                        },
                      }}
                    />
                  }
                  sx={{
                    width: "100%",
                    m: 0,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                  label={<Typography sx={typography.bodyLarge}>{option.title}</Typography>}
                />
              ))}
            </RadioGroup>
          </FormControl>
        );

      case "CheckboxGroup":
        return (
          <FormControl
            key={index}
            component="fieldset"
            sx={{ mb: 2, width: "100%" }}
          >
            <FormLabel component="legend" sx={{ ...typography.bodyLarge, mb: 1 }}>
              {element.label}
              {element.required && (
                <Typography component="span" color="error" sx={typography.bodyLarge}>
                  {" "}
                  *
                </Typography>
              )}
            </FormLabel>

            <FormGroup>
              {element["data-source"]?.map((option) => (
                <FormControlLabel
                  key={option.id}
                  labelPlacement="start"
                  control={
                    <Checkbox
                      checked={
                        formData[element.name]?.includes(option.id) || false
                      }
                      onChange={(e) => {
                        const currentValues = formData[element.name] || [];
                        const newValues = e.target.checked
                          ? [...currentValues, option.id]
                          : currentValues.filter((id) => id !== option.id);

                        handleInputChange(element.name, newValues);
                      }}
                      sx={{
                        color: ColorsConstants.primaryGray20,
                        "&.Mui-checked": {
                          color: formConstants.greenButton,
                        },
                      }}
                    />
                  }
                  sx={{
                    width: "100%",
                    m: 0,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                  label={<Typography sx={typography.bodyLarge}>{option.title}</Typography>}
                />
              ))}
            </FormGroup>
          </FormControl>
        );

      case "Dropdown":
        return (
          <Box
            key={index}
            sx={{
              mb: 2,
              p: 1.5,
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              cursor: "pointer",
            }}
            onClick={() => {
              navigateToScreen(`${element.name}_OPTIONS`);
            }}
          >
            <Typography sx={{ ...typography.bodySmall, color: "text.secondary" }}>
              {element.label}
              {element.required && " *"}
            </Typography>

            <Typography sx={typography.bodyLarge}>
              {
                element["data-source"]?.find(
                  opt => opt.id === formData[element.name]
                )?.title
              }
            </Typography>
          </Box>
        );

      case "DatePicker":
        return (
          <TextField
            key={index}
            label={element.label}
            name={element.name}
            required={element.required}
            type="date"
            fullWidth
            size="small"
            value={formData[element.name] || ""}
            onChange={(e) => handleInputChange(element.name, e.target.value)}
            InputLabelProps={{ shrink: true, sx: typography.bodyLarge }}
            inputProps={{ style: typography.bodyLarge }}
            sx={{ mb: 2 }}
          />
        );

      case "OptIn":
        return (
          <FormControlLabel
            key={index}
            control={
              <Checkbox
                checked={formData[element.name] || false}
                onChange={(e) =>
                  handleInputChange(element.name, e.target.checked)
                }
                required={element.required}
                sx={{
                  color: ColorsConstants.primaryGray20,
                  "&.Mui-checked": {
                    color: formConstants.greenButton,
                  },
                }}
              />
            }
            label={<Typography sx={typography.bodyLarge}>{element.label}</Typography>}
            sx={{ mb: 2 }}
          />
        );

      case "If": {
        // Published "Show only when" content: "${form|data.x} op value".
        const m = /^\$\{(?:form|data)\.(\w+)\}\s*(==|!=|>=|<=|>|<)\s*'?([^']*)'?$/.exec(
          element.condition || "",
        );
        const shown = m && isVisible({ "visible-when": [{ field: m[1], op: m[2], value: m[3] }] });
        return (shown ? element.then : element.else || []).map((child, childIndex) =>
          renderFormElement(child, `${index}-${childIndex}`),
        );
      }

      case "Switch": {
        // Show the case for the selected answer (${form.x} on this screen or
        // ${data.x} passed in from an earlier one). Until one is picked, a
        // button Switch shows its first button disabled; a questions Switch
        // shows nothing (as on WhatsApp).
        const field = element.value?.match(/^\$\{(?:form|data)\.(.+)\}$/)?.[1];
        const cases = element.cases || {};
        const selected = cases[formData[field]];
        const fallback = isNavSwitch(element) && Object.values(cases)[0]?.[0];
        return selected
          ? selected.map((child, childIndex) =>
              renderFormElement(child, `${index}-${childIndex}`),
            )
          : fallback && renderFormElement({ ...fallback, disabled: true }, index);
      }

      case "Footer":
        return (
          <Button
            key={index}
            variant="contained"
            fullWidth
            disabled={element.disabled}
            onClick={() => {
              const action = element["on-click-action"];
              if (action?.name === "navigate" && action.next?.name) {
                navigateToScreen(action.next.name);
              }
            }}
            sx={{
              mt: 2,
              mb: 0,
              px: 2,
              bgcolor: formConstants.greenButton,
              ...typography.bodyLarge,
              fontWeight: 600,
              "&:hover": {
                bgcolor: ColorsConstants.primaryGray60,
              },
              borderRadius: 50,
              zIndex: 10,              
            }}
          >
            {element.label || "Submit"}
          </Button>
        );

      case "Form":
        return (
          <Box key={index} component="form" sx={{ width: "100%" }}>
            {element.children?.map((child, childIndex) =>
              renderFormElement(child, `${index}-${childIndex}`),
            )}
          </Box>
        );

      case "EmbeddedLink":
        return (
          <Button
            key={index}
            variant="text"
            href={element.url}
            target="_blank"
            sx={{ 
              mb: 2, 
              textTransform: "capitalize",
              ...typography.bodyLarge,
              color: formConstants.greenColor,
            }}
          >
            {element.text}
          </Button>
        );

      case "Image":
        return (
          <Box
            key={index}
            component="img"
            src={
              /^https?:/i.test(element.src || "")
                ? element.src
                : `data:image/png;base64,${element.src}`
            }
            alt={element.alt || element["alt-text"] || "Form image"}
            sx={{
              width: "100%",
              maxWidth: element.width || "100%",
              height: "auto",
              mb: 2,
              borderRadius: 1,
            }}
          />
        );

      default:
        return null;
    }
  };

  const renderScreen = () => {
    if (currentScreenId?.endsWith("_OPTIONS")) {
      const fieldName = currentScreenId.replace("_OPTIONS", "");

      const dropdownElement = flattenElements(
        screens?.flatMap(s => s.layout?.children?.[0]?.children || []) || []
      ).find(el => el.name === fieldName);

      if (!dropdownElement) return null;

      return (
        <Stack sx={{ p: 2, height: "100%", overflow: "auto" }}>
          <BackButton onClick={goBack} />

          {dropdownElement["data-source"]?.map(option => (
            <Box
              key={option.id}
              sx={{
                p: 2,
                borderBottom: 1,
                borderColor: "divider",
                cursor: "pointer",
              }}
              onClick={() => {
                handleInputChange(fieldName, option.id);
                goBack();
              }}
            >
              <Typography sx={typography.bodyLarge}>
                {option.title}
              </Typography>
            </Box>
          ))}
        </Stack>
      );
    }
    
    if (!currentScreen) return null;

    const layout = currentScreen.layout;
    
    return (
      <Box sx={{ 
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}>

        {/* ── PINNED HEADER ── */}
        <Box sx={{
          flexShrink: 0,
          py: 2,
          px: 2,
          borderBottom: "1px solid #ebe3e3ff",
          bgcolor: "#fff",
          display: "flex",
          flexDirection: "row",
          justifyContent: currentScreen.terminal ? "space-between" : "center",
          alignItems: "center",
        }}>
          <Typography sx={{ ...typography.h2, textAlign: "center", color: "#0A0A0A" }}>
            {currentScreen.title}
          </Typography>
          {currentScreen.terminal && (
            <Box sx={{
              py: 0.25,
              px: 1,
              bgcolor: currentScreen.success ? "success.light" : "error.light",
              borderRadius: 100,
              textAlign: "center",
              color: "#fff",
            }}>
              <Typography sx={{ ...typography.bodySmall, fontWeight: "bold" }}>
                {currentScreen.success ? "Final Screen" : "Terminal Screen"}
              </Typography>
            </Box>
          )}
        </Box>

        {/* ── SCROLLABLE MIDDLE ── */}
        {layout?.type === "SingleColumnLayout" && (
          <>
            {layout.children?.map((child, index) => {
              if (child.type === "Form") {
                const formChildren = child.children || [];
                // A questions Switch is content; only a Switch of buttons is the footer.
                const isFooter = (el) => el.type === 'Footer' || isNavSwitch(el);
                const nonFooterElements = formChildren.filter(el => !isFooter(el));
                const footerElement = formChildren.find(isFooter);

                return (
                  <React.Fragment key={index}>
                    {/* Scrollable content */}
                    <Box sx={{
                      flex: 1,
                      overflowY: "auto",
                      overflowX: "hidden",
                      px: 2,
                      py: 1,
                      minHeight: 0,
                    }}>
                      {nonFooterElements.map((el, idx) =>
                        renderFormElement(el, `${index}-${idx}`)
                      )}
                    </Box>

                    {/* ── PINNED FOOTER ── */}
                    {footerElement && (
                      <Box sx={{
                        flexShrink: 0,
                        px: 2,
                        pt: 0.5,
                        pb: 1.5,
                        borderTop: "1px solid #ebe3e3ff",
                        bgcolor: "#fff",
                      }}>
                        {renderFormElement(footerElement, `${index}-footer`)}
                      </Box>
                    )}
                  </React.Fragment>
                );
              }

              return (
                <Box key={index} sx={{ flex: 1, overflowY: "auto", px: 2, py: 1, minHeight: 0 }}>
                  {renderFormElement(child, index)}
                </Box>
              );
            })}
          </>
        )}
      </Box>
    );
  };
  
  return (
    <Stack sx={{ 
      width: compact ? "100%" : "500px",
      p: compact ? 0 : 2,
      pt: compact ? 0 : 3,
      minHeight: compact ? 0 : "700px",
      height: compact ? "auto" : "700px",
      "& .Mui-focused": {
        color: `${formConstants.greenColor} !important`,
      },
      "& .MuiOutlinedInput-root": {
        "&.Mui-focused fieldset": {
          borderColor: `${formConstants.greenColor} !important`,
        },
      },
      "& .MuiInputLabel-root.Mui-focused": {
        color: `${formConstants.greenColor} !important`,
      },
      "& .MuiFormLabel-root.Mui-focused": {
        color: `${formConstants.greenColor} !important`,
      },
      "& .MuiCheckbox-root.Mui-checked": {
        color: `${formConstants.greenColor} !important`,
      },
      "& .MuiRadio-root.Mui-checked": {
        color: `${formConstants.greenColor} !important`,
      },
      "& .MuiSelect-icon": {
        color: formConstants.greenColor,
      }, 
    }}>
      {!compact && (
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={5}
      >
        <Typography sx={{ ...typography.h2, fontWeight: "bold" }}>
          Preview
        </Typography>

        <Autocomplete
          options={screens || []}
          value={screens?.find(s => s.id === currentScreenId) || null}
          onChange={(e, newValue) => {
            if (newValue?.id) {
              setScreenStack([newValue.id]); // reset stack to selected screen
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Screen"
              variant="outlined"
              size="small"
              fullWidth
              InputLabelProps={{ sx: typography.bodyLarge }}
              inputProps={{ ...params.inputProps, style: typography.bodyLarge }}
            />
          )}
          getOptionLabel={(option) => formatTitle(option?.title) || ""}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          noOptionsText="No Screens Found"
          sx={{ flex: 0.8 }}
          disabled={!screens || screens.length === 0}
          renderOption={(props, option) => (
            <li {...props}>
              <Typography sx={typography.bodyLarge}>
                {formatTitle(option?.title)}
              </Typography>
            </li>
          )}
        />
      </Stack>
      )}
      <Box sx={{ display: 'flex', justifyContent: "center", height: compact ? "auto" : "calc(100% - 60px)" }}>
        <Box
          sx={{
            width: compact ? "min(100%, 300px)" : "80%",
            height: "560px",        
            minHeight: "560px",
            borderRadius: 4,
            border: 4,
            borderColor: ColorsConstants.primaryGray20,
            overflow: "hidden",
            bgcolor: "#fff",
          }}
        >
          {formJson && screenStack.length > 0 ? (
            renderScreen()
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: 2,
                height: "100%",
              }}
            >
              <Info sx={{ fontSize: 36, color: ColorsConstants.primaryGray40 }} />
              <Typography sx={{ ...typography.bodyLarge, color: ColorsConstants.primaryGray60 }}>
                Missing or Invalid Form Data
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Stack>
  );
};

export default FormPreview;